import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { ArrowLeft, Link2, X, ExternalLink, ImageIcon, Calendar } from 'lucide-react';
import { newsroomApi, LinkPreview } from '../services/newsroomApi';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

export const NewsroomNewArticlePage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();

  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<LinkPreview | null>(null);
  const [title, setTitle] = useState('');
  const [channelId, setChannelId] = useState('');
  const [publishedAt, setPublishedAt] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isFetching, setIsFetching] = useState(false);

  const { data: channels = [] } = useQuery({
    queryKey: ['newsroom-channels'],
    queryFn: newsroomApi.getChannels,
  });

  const handleFetchPreview = async () => {
    if (!url.trim()) return;

    let fetchUrl = url.trim();
    if (!fetchUrl.startsWith('http://') && !fetchUrl.startsWith('https://')) {
      fetchUrl = 'https://' + fetchUrl;
      setUrl(fetchUrl);
    }

    setIsFetching(true);
    try {
      const data = await newsroomApi.fetchPreview(fetchUrl);
      setPreview(data);
      if (data.title) setTitle(data.title);
      toast.success(t('newsroom.infoFetched'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('newsroom.fetchError'));
    } finally {
      setIsFetching(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: newsroomApi.createArticle,
    onSuccess: () => {
      toast.success(t('newsroom.articlePosted'));
      navigate('/newsroom');
    },
    onError: (err: any) => {
      const msgs = err?.response?.data?.errors;
      if (msgs?.length) {
        toast.error(msgs[0]);
      } else {
        toast.error(err?.response?.data?.message || t('newsroom.error'));
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview) { toast.error(t('newsroom.fetchFirst')); return; }
    if (!channelId) { toast.error(t('newsroom.selectChannelRequired')); return; }
    if (!title.trim() || title.trim().length < 3) { toast.error(t('newsroom.titleRequired')); return; }

    createMutation.mutate({
      external_url: preview.url,
      title: title.trim(),
      channel_id: channelId,
      og_image_url: preview.image_url || undefined,
      description: preview.description || undefined,
      published_at: publishedAt ? new Date(publishedAt).toISOString() : undefined,
    });
  };

  const resetPreview = () => {
    setPreview(null);
    setTitle('');
    setUrl('');
    setPublishedAt(format(new Date(), 'yyyy-MM-dd'));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <button
          onClick={() => navigate('/newsroom')}
          className="hover:text-[#0B1F3A] flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {t('newsroom.title')}
        </button>
        <span>›</span>
        <span className="text-gray-700">{t('newsroom.shareLink')}</span>
      </div>

      <Card>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{t('newsroom.newArticleTitle')}</h1>
        <p className="text-sm text-gray-500 mb-6">{t('newsroom.newArticleDesc')}</p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('newsroom.articleUrl')} <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); if (preview) setPreview(null); }}
                onKeyDown={(e) => e.key === 'Enter' && handleFetchPreview()}
                placeholder="https://www.example.com/artikel..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
              />
            </div>
            <Button
              type="button"
              onClick={handleFetchPreview}
              loading={isFetching}
              disabled={!url.trim() || !!preview}
              variant="outline"
            >
              {t('newsroom.fetch')}
            </Button>
          </div>
        </div>

        {preview && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{t('newsroom.preview')}</span>
              <button
                onClick={resetPreview}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" /> {t('newsroom.otherLink')}
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              {preview.image_url ? (
                <div className="aspect-video overflow-hidden bg-gray-100">
                  <img
                    src={preview.image_url}
                    alt={preview.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              ) : (
                <div className="aspect-video bg-gray-100 flex items-center justify-center">
                  <ImageIcon className="w-10 h-10 text-gray-300" />
                </div>
              )}
              <div className="p-3">
                <p className="text-xs text-gray-400 truncate mb-1">{preview.url}</p>
                {preview.description && (
                  <p className="text-sm text-gray-500 line-clamp-2">{preview.description}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {preview && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('newsroom.titleLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('newsroom.titlePlaceholder')}
                maxLength={255}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                required
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{title.length}/255</p>
            </div>
          )}

          {preview && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('newsroom.channelLabel')} <span className="text-red-500">*</span>
              </label>
              <select
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                required
              >
                <option value="">{t('newsroom.selectChannel')}</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {preview && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {t('newsroom.publicationDate')} <span className="text-red-500">*</span>
                </span>
              </label>
              <input
                type="date"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
                max={format(new Date(), 'yyyy-MM-dd')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                required
              />
              <p className="text-xs text-gray-400 mt-1">{t('newsroom.publicationDateHint')}</p>
            </div>
          )}

          {preview && (
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate('/newsroom')}>
                {t('newsroom.cancel')}
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('newsroom.submitArticle')}
              </Button>
            </div>
          )}
        </form>

        {!preview && (
          <div className="text-center py-8 text-gray-400">
            <Link2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm" dangerouslySetInnerHTML={{ __html: t('newsroom.pasteUrlHint') }} />
          </div>
        )}
      </Card>
    </div>
  );
};
